import { MYDRA_WORKSPACE_ID_HEADER_NAME } from '@/config/system'
import { ApiError } from '@/services/api'
import { PlaybooksService } from '@/services/playbook'
import { useWorkspaceContext } from '@/shared/hooks/useWorkspaceContext'
import {
  ApiErrorResponse,
  ApiIndividualResponse,
  ApiReactMutationOptions,
} from '@/types/api'
import {
  ApiPlaybookCreationRequest,
  ApiPlaybookCreationResponse,
} from '@/types/playbook'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useUpgradePlanModal } from '../../useUpgradePlanModal'

export const usePlaybookCreation = (
  mutationOptions?: ApiReactMutationOptions<
    ApiIndividualResponse<ApiPlaybookCreationResponse>,
    ApiPlaybookCreationRequest
  >
) => {
  const queryClient = useQueryClient()
  const { workspaceId } = useWorkspaceContext()
  const { show: showUpgradePlanModal } = useUpgradePlanModal()

  return useMutation<
    ApiIndividualResponse<ApiPlaybookCreationResponse>,
    ApiErrorResponse,
    ApiPlaybookCreationRequest
  >({
    ...mutationOptions,
    mutationKey: [
      'playbooks',
      'create',
      ...(workspaceId ? ['workspaceId', workspaceId] : []),
    ],
    mutationFn: (playbookData: ApiPlaybookCreationRequest) =>
      PlaybooksService.createPlaybook(playbookData, {
        headers: {
          [MYDRA_WORKSPACE_ID_HEADER_NAME]: workspaceId,
        },
      }),
    onSuccess: async (...args) => {
      queryClient.invalidateQueries({
        queryKey: ['playbooks'],
      })
      await mutationOptions?.onSuccess?.(...args)
    },
    onError: async (error, ...args) => {
      if (error instanceof ApiError && error.statusCode === 429) {
        showUpgradePlanModal('limited_playbooks')
      }
      await mutationOptions?.onError?.(error, ...args)
    },
  })
}
